import React from "react";
import { I } from "./icons";
import { L } from "./helpers";
import logoImg from "@/assets/nutrimarket-logo.png?w=64&format=webp";
import { useNav } from "./context/NavContext";
import { CategoryMegaMenu } from "./CategoryMegaMenu";
import { motion, AnimatePresence } from "framer-motion";
import type { Lang, Notif, Translations, PrefetchFn, Product } from "./types";
import { LoyaltyPill } from "./shared/LoyaltyPill";

interface TopBarProps {
  sideOpen: boolean;
  setSideOpen: (v: boolean | ((p: boolean) => boolean)) => void;
  search: string;
  setSearch: (v: string) => void;
  setSearchOpen: (v: boolean) => void;
  lang: Lang;
  setLang: (fn: (l: Lang) => Lang) => void;
  darkMode: boolean;
  setDarkMode: (fn: (d: boolean) => boolean) => void;
  notifs: Notif[];
  showNotifs: boolean;
  setShowNotifs: (v: boolean) => void;
  setNotifs: (fn: (p: Notif[]) => Notif[]) => void;
  setCartOpen: (v: boolean) => void;
  cartCnt: number;
  t: Translations;
  prefetchDashboard?: PrefetchFn;
  P?: Product[];
  setCatF?: (id: string) => void;
  rewardPts?: number;
  authed?: boolean;
}

export function TopBar({
  sideOpen, setSideOpen, search, setSearch, setSearchOpen,
  lang, setLang, darkMode, setDarkMode, notifs, showNotifs, setShowNotifs,
  setNotifs, setCartOpen, cartCnt, t, prefetchDashboard, P, setCatF,
  rewardPts = 0, authed = false,
}: TopBarProps) {
  const { nav } = useNav();
  return (
    <header className="tp">
      <button
        className="mb"
        onClick={() => setSideOpen(!sideOpen)}
        aria-label={lang === "es" ? "Abrir menú" : "Open menu"}
        aria-expanded={sideOpen}
      >
        {I.menu}
      </button>
      <button
        className="hbtn"
        onClick={() => nav("dashboard")}
        onMouseEnter={prefetchDashboard}
        onTouchStart={prefetchDashboard}
        aria-label={t.dashboard}
      >
        <img src={logoImg} alt="NutriMarket" width={22} height={22} style={{ objectFit: "contain" }} />
        <span className="hbtn-label">{t.dashboard}</span>
      </button>
      {P && setCatF && <CategoryMegaMenu lang={lang} P={P} setCatF={setCatF} />}
      <div className="sb" onClick={() => setSearchOpen(true)}>
        {I.search}
        <input
          placeholder={t.searchPh}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setSearchOpen(true)}
        />
      </div>
      <div className="ta">
        <LoyaltyPill rewardPts={rewardPts} lang={lang} authed={authed} />
        <button
          className="dm-toggle"
          onClick={() => setDarkMode((d) => !d)}
          aria-label={darkMode ? (lang === "es" ? "Cambiar a modo claro" : "Switch to light mode") : (lang === "es" ? "Cambiar a modo oscuro" : "Switch to dark mode")}
          aria-pressed={darkMode}
        >
          {darkMode ? "☀️" : "🌙"}
        </button>
        <button
          className="ib"
          onClick={() => setLang((l) => (l === "en" ? "es" : "en"))}
          style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}
          aria-label={lang === "en" ? "Cambiar a español" : "Switch to English"}
          title={lang === "en" ? "ES" : "EN"}
        >
          <span aria-hidden="true">{lang === "en" ? "🇩🇴" : "🇺🇸"}</span>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
            {lang === "en" ? "ES" : "EN"}
          </span>
        </button>
        <div style={{ position: "relative" }}>
          <button className="ib" onClick={() => setShowNotifs(!showNotifs)} aria-label={t.notifCenter}>
            {I.bell}
            <AnimatePresence>
              {notifs.filter((n) => !n.read).length > 0 && (
                <motion.span
                  key={notifs.filter((n) => !n.read).length}
                  className="cc"
                  initial={{ scale: 0.4, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.4, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 18 }}
                >
                  {Math.min(99, notifs.filter((n) => !n.read).length)}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
          {showNotifs && (
            <div className="notif-panel">
              <div className="notif-hdr">
                <h4>{t.notifCenter}</h4>
                <button onClick={() => setNotifs((p) => p.map((n) => ({ ...n, read: true })))}>
                  {t.notifMarkRead}
                </button>
              </div>
              <div className="notif-list">
                {notifs.slice(0, 4).map((n) => (
                  <div
                    key={n.id}
                    className={`notif-item ${!n.read ? "unread" : ""}`}
                    onClick={() => {
                      setNotifs((p) => p.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
                      const url = (n as any).action_url as string | null;
                      if (n.prod) nav("detail", n.prod);
                      else if (url) {
                        if (url.startsWith("/")) window.location.assign(url);
                        else window.open(url, "_blank", "noopener");
                      }
                      setShowNotifs(false);
                    }}
                  >
                    <div className="notif-ic">{n.icon}</div>
                    <div className="notif-body">
                      <b>{(n as any).title || t[n.title_key]}</b>
                      <p>
                        {n.extra ? `${n.extra} ` : ""}
                        {n.prod ? `${L(n.prod.nm, lang)} ` : ""}
                        {(n as any).msg || t[n.msg_key]}
                      </p>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                      <span className="notif-time">{n.time}</span>
                      {!n.read && <span className="notif-dot2" />}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ padding: "8px 14px", borderTop: "1px solid var(--bd)", textAlign: "center" }}>
                <button className="lk" onClick={() => { nav("notifications"); setShowNotifs(false); }}>
                  {t.seeAll}
                </button>
              </div>
            </div>
          )}
        </div>
        <button
          className="ib"
          data-testid="topbar-cart-btn"
          onClick={() => setCartOpen(true)}
          aria-label={`${t.cart || (lang === "es" ? "Carrito" : "Cart")}${cartCnt > 0 ? ` (${cartCnt})` : ""}`}
        >
          {I.cart}
          <AnimatePresence>
            {cartCnt > 0 && (
              <motion.span
                key={cartCnt}
                className="cc"
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.4, opacity: 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 18 }}
              >
                {cartCnt}
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </header>
  );
}